import React, { useState, useEffect } from 'react';
import { timetableService } from '../../lib/services/timetable';
import TimetableGrid from './TimetableGrid';
import './TimetableView.css';

const TimetableView = ({ 
  type, // 'class', 'teacher', 'student'
  entityId, // ID of the class, teacher, or student
  entityName, // Name for display
  onClose,
  showCloseButton = true 
}) => {
  const [timetableData, setTimetableData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Time slots and day names with proper sequence including breaks
  const timeSlots = [
    '7:45-8:10',  // Master Class Period (before Period 1)
    '8:10-8:45',  // Period 1
    '8:45-9:20',  // Period 2
    '9:20-9:55',  // Period 3
    '9:55-10:15', // Recess (between Periods 3-4)
    '10:15-10:50', // Period 4
    '10:50-11:25', // Period 5
    '11:25-11:30', // Recess (between Periods 5-6)
    '11:30-12:05', // Period 6
    '12:05-12:40', // Period 7
    '12:40-1:50',  // Lunch (between Periods 7-8)
    '1:50-2:25',   // Period 8
    '2:25-3:00',   // Period 9
    '3:00-3:35',   // Period 10
    '3:35-4:00'    // Master Class Period (after Period 10)
  ];

  const dayNames = ['星期一', '星期二', '星期三', '星期四', '星期五'];
  const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

  useEffect(() => {
    loadTimetableData();
  }, [type, entityId]);

  const loadTimetableData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 TimetableView: Loading timetable data...');
      console.log('📋 Type:', type);
      console.log('🆔 Entity ID:', entityId);
      console.log('📝 Entity Name:', entityName);

      let data = null;

      switch (type) {
        case 'class':
          console.log('📚 Fetching class timetable for:', entityId);
          data = await timetableService.getClassTimetable(entityId);
          console.log('✅ Class timetable data received:', data);
          break;
        case 'teacher':
          console.log('👨‍🏫 Fetching teacher timetable for:', entityId);
          data = await timetableService.getTeacherTimetable(entityId);
          console.log('✅ Teacher timetable data received:', data);
          break;
        case 'student':
          // For students, we get their class timetable
          data = await getStudentTimetable(entityId);
          break;
        default:
          throw new Error('Invalid timetable type');
      }

      console.log('📊 Raw timetable data received:', data);
      
      // Organize the data with proper sequence
      const organizedData = organizeTimetableData(data);
      console.log('📊 Organized timetable data being set:', organizedData);
      setTimetableData(organizedData);
    } catch (err) {
      console.error('❌ Error loading timetable data:', err);
      setError('載入時間表時發生錯誤');
    } finally {
      setLoading(false);
    }
  };


  const getStudentTimetable = async (studentId) => {
    try {
      // Get student's class information
      const students = await timetableService.getAllStudents();
      const student = students.find(s => s.id === studentId);
      
      if (!student || !student.class_id) {
        return createEmptyTimetable();
      }

      return await timetableService.getClassTimetable(student.class_id);
    } catch (error) {
      console.error('Error getting student timetable:', error);
      return createEmptyTimetable();
    }
  };

  const createEmptyTimetable = () => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const timetable = {};
    
    days.forEach(day => {
      timetable[day] = timeSlots.map((timeSlot, index) => {
        // Determine the type based on time slot
        let subject = '';
        let type = 'core';
        
        if (timeSlot === '7:45-8:10' || timeSlot === '3:35-4:00') {
          subject = 'Master Class Period';
          type = 'master';
        } else if (timeSlot === '9:55-10:15' || timeSlot === '11:25-11:30') {
          subject = 'Recess';
          type = 'break';
        } else if (timeSlot === '12:40-1:50') {
          subject = 'Lunch';
          type = 'break';
        }
        
        return {
          time: timeSlot,
          subject: subject,
          classroom: '',
          teacher: '',
          type: type
        };
      });
    });

    return timetable;
  };

  // Function to organize timetable data with proper sequence
  const organizeTimetableData = (rawData) => {
    if (!rawData) return createEmptyTimetable();
    
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const dayMapping = {
      'Monday': 'monday',
      'Tuesday': 'tuesday', 
      'Wednesday': 'wednesday',
      'Thursday': 'thursday',
      'Friday': 'friday'
    };
    
    const organizedData = {};
    
    days.forEach(day => {
      organizedData[day] = timeSlots.map((timeSlot, index) => {
        // Find matching data for this time slot
        const matchingData = rawData[day]?.find(entry => entry.time_slot === timeSlot);
        
        if (matchingData) {
          return {
            time: timeSlot,
            subject: matchingData.subject,
            classroom: matchingData.classroom || '',
            teacher: matchingData.teacher_name || '',
            type: matchingData.subject_type || 'core',
            class_id: matchingData.class_id || '',
            notes: matchingData.notes || ''
          };
        } else {
          // Create appropriate empty/special period
          let subject = '';
          let type = 'core';
          
          if (timeSlot === '7:45-8:10' || timeSlot === '3:35-4:00') {
            subject = 'Master Class Period';
            type = 'master';
          } else if (timeSlot === '9:55-10:15' || timeSlot === '11:25-11:30') {
            subject = 'Recess';
            type = 'break';
          } else if (timeSlot === '12:40-1:50') {
            subject = 'Lunch';
            type = 'break';
          } else {
            subject = 'Empty';
            type = 'empty';
          }
          
          return {
            time: timeSlot,
            subject: subject,
            classroom: '',
            teacher: '',
            type: type,
            class_id: '',
            notes: ''
          };
        }
      });
    });
    
    return organizedData;
  };

  const getTimetableTitle = () => {
    switch (type) {
      case 'class':
        return `${entityName} - 班級時間表`;
      case 'teacher':
        return `${entityName} - 教師時間表`;
      case 'student':
        return `${entityName} - 學生時間表`;
      default:
        return '時間表';
    }
  };

  if (loading) {
    return (
      <div className="timetable-view">
        <div className="timetable-header">
          <h3>{getTimetableTitle()}</h3>
          {showCloseButton && onClose && (
            <button className="close-btn" onClick={onClose}>×</button>
          )}
        </div>
        <div className="timetable-loading">
          <div className="loading-spinner"></div>
          <p>載入時間表中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="timetable-view">
        <div className="timetable-header">
          <h3>{getTimetableTitle()}</h3>
          {showCloseButton && onClose && (
            <button className="close-btn" onClick={onClose}>×</button>
          )}
        </div>
        <div className="timetable-error">
          <p>❌ {error}</p>
          <button onClick={loadTimetableData} className="retry-btn">
            重新載入
          </button>
        </div>
      </div>
    );
  }

  if (!timetableData || Object.keys(timetableData).length === 0) {
    return (
      <div className="timetable-view">
        <div className="timetable-header">
          <h3>{getTimetableTitle()}</h3>
          {showCloseButton && onClose && (
            <button className="close-btn" onClick={onClose}>×</button>
          )}
        </div>
        <div className="timetable-empty">
          <p>📅 暫無時間表資料</p>
        </div>
      </div>
    );
  }

  return (
    <div className="timetable-view">
      <div className="timetable-header">
        <h3>{getTimetableTitle()}</h3>
        {showCloseButton && onClose && (
          <button className="close-btn" onClick={onClose}>×</button>
        )}
      </div>
      <div className="timetable-content">
        <TimetableGrid
          data={timetableData}
          title=""
          hasElectives={type === 'class' && entityName?.includes('Form 6')}
          timeSlots={timeSlots}
          dayNames={dayNames}
          dayKeys={dayKeys}
        />
      </div>
    </div>
  );
};

export default TimetableView;
